import { LLMID } from '@/types'
import {
  CLAUDE_FABLE_5_1_MODEL_ID,
  CLAUDE_FABLE_5_MODEL_ID,
  CLAUDE_HAIKU_4_5_MODEL_ID,
  CLAUDE_OPUS_4_5_MODEL_ID,
  CLAUDE_OPUS_4_6_MODEL_ID,
  CLAUDE_OPUS_4_8_MODEL_ID,
  CLAUDE_OPUS_5_MODEL_ID,
  CLAUDE_SONNET_5_MODEL_ID
} from '@/lib/anthropic/model-capabilities'

type ChatSettingLimits = {
  MIN_TEMPERATURE: number
  MAX_TEMPERATURE: number
  MAX_TOKEN_OUTPUT_LENGTH: number
  MAX_CONTEXT_LENGTH: number
}

const DEFAULT_MAX_TOKEN_OUTPUT_LENGTH = 8192

export const CHAT_SETTING_LIMITS: Record<LLMID, ChatSettingLimits> = {
  'xai.grok-4.3': {
    MIN_TEMPERATURE: 0.0,
    MAX_TEMPERATURE: 1.0,
    MAX_TOKEN_OUTPUT_LENGTH: 64000,
    MAX_CONTEXT_LENGTH: 1000000
  },

  // ANTHROPIC MODELS (current: Fable 5.1 / Opus 5 / Sonnet 5 / Haiku 4.5)
  [CLAUDE_FABLE_5_1_MODEL_ID]: {
    MIN_TEMPERATURE: 0.0,
    MAX_TEMPERATURE: 1.0,
    MAX_TOKEN_OUTPUT_LENGTH: 128000,
    MAX_CONTEXT_LENGTH: 1000000
  },
  [CLAUDE_FABLE_5_MODEL_ID]: {
    MIN_TEMPERATURE: 0.0,
    MAX_TEMPERATURE: 1.0,
    MAX_TOKEN_OUTPUT_LENGTH: 128000,
    MAX_CONTEXT_LENGTH: 1000000
  },
  [CLAUDE_OPUS_5_MODEL_ID]: {
    MIN_TEMPERATURE: 0.0,
    MAX_TEMPERATURE: 1.0,
    MAX_TOKEN_OUTPUT_LENGTH: 128000,
    MAX_CONTEXT_LENGTH: 1000000
  },
  [CLAUDE_OPUS_4_8_MODEL_ID]: {
    MIN_TEMPERATURE: 0.0,
    MAX_TEMPERATURE: 1.0,
    MAX_TOKEN_OUTPUT_LENGTH: 128000,
    MAX_CONTEXT_LENGTH: 1000000
  },
  // Capacity-fallback siblings (LOSI_CLAUDE_DAILY_LIMIT_FALLBACK_CHAIN).
  // Must have explicit caps so Claude Max's 128k budget is clamped per hop.
  // Bedrock model cards (2026): Opus 4.6 = 128K, Opus 4.5 = 64K, Sonnet 4.5 = 64K.
  [CLAUDE_OPUS_4_6_MODEL_ID]: {
    MIN_TEMPERATURE: 0.0,
    MAX_TEMPERATURE: 1.0,
    MAX_TOKEN_OUTPUT_LENGTH: 128000,
    MAX_CONTEXT_LENGTH: 1000000
  },
  [CLAUDE_OPUS_4_5_MODEL_ID]: {
    MIN_TEMPERATURE: 0.0,
    MAX_TEMPERATURE: 1.0,
    // AWS Bedrock card: Max output tokens 64K (not 128K).
    MAX_TOKEN_OUTPUT_LENGTH: 64000,
    MAX_CONTEXT_LENGTH: 200000
  },
  [CLAUDE_SONNET_5_MODEL_ID]: {
    MIN_TEMPERATURE: 0.0,
    MAX_TEMPERATURE: 1.0,
    // Losi Max → Bedrock `global.anthropic.claude-sonnet-4-6` (200k context /
    // 64k output). Inflating this to 1M made tools-stream keep ~300k+ tokens
    // and Bedrock rejected with "prompt is too long … > 200000 maximum".
    MAX_TOKEN_OUTPUT_LENGTH: 64000,
    MAX_CONTEXT_LENGTH: 200000
  },
  [CLAUDE_HAIKU_4_5_MODEL_ID]: {
    MIN_TEMPERATURE: 0.0,
    MAX_TEMPERATURE: 1.0,
    // AWS Bedrock + Anthropic: Max output 64K (was under-capped at 8192).
    MAX_TOKEN_OUTPUT_LENGTH: 64000,
    MAX_CONTEXT_LENGTH: 200000
  },
  'claude-sonnet-4-5-20250929': {
    MIN_TEMPERATURE: 0.0,
    MAX_TEMPERATURE: 1.0,
    // Last Claude sibling before Bedrock escape — Bedrock card: 64K.
    MAX_TOKEN_OUTPUT_LENGTH: 64000,
    MAX_CONTEXT_LENGTH: 200000
  },
  'claude-3-opus-20240229': {
    MIN_TEMPERATURE: 0.0,
    MAX_TEMPERATURE: 1.0,
    MAX_TOKEN_OUTPUT_LENGTH: 4096,
    MAX_CONTEXT_LENGTH: 200000
  },

  // GOOGLE MODELS
  'gemini-pro': {
    MIN_TEMPERATURE: 0.0,
    MAX_TEMPERATURE: 1.0,
    MAX_TOKEN_OUTPUT_LENGTH: 2048,
    MAX_CONTEXT_LENGTH: 30720
  },
  'gemma-3-27b-it': {
    MIN_TEMPERATURE: 0.0,
    MAX_TEMPERATURE: 1.0,
    MAX_TOKEN_OUTPUT_LENGTH: 8192,
    MAX_CONTEXT_LENGTH: 131072
  },
  'gemini-2.0-flash-exp': {
    MIN_TEMPERATURE: 0.0,
    MAX_TEMPERATURE: 1.0,
    MAX_TOKEN_OUTPUT_LENGTH: 4096,
    MAX_CONTEXT_LENGTH: 1000000
  },
  'gemini-2.0-flash-thinking-exp-1219': {
    MIN_TEMPERATURE: 0.0,
    MAX_TEMPERATURE: 1.0,
    MAX_TOKEN_OUTPUT_LENGTH: 8000,
    MAX_CONTEXT_LENGTH: 32000
  },
  'gemini-1.5-pro-latest': {
    MIN_TEMPERATURE: 0.0,
    MAX_TEMPERATURE: 1.0,
    MAX_TOKEN_OUTPUT_LENGTH: 4096,
    MAX_CONTEXT_LENGTH: 1000000
  },
  'gemini-1.5-flash-latest': {
    MIN_TEMPERATURE: 0.0,
    MAX_TEMPERATURE: 1.0,
    MAX_TOKEN_OUTPUT_LENGTH: 4096,
    MAX_CONTEXT_LENGTH: 1000000
  },
  'gemini-2.5-pro': {
    MIN_TEMPERATURE: 0.0,
    MAX_TEMPERATURE: 1.0,
    MAX_TOKEN_OUTPUT_LENGTH: 65536,
    MAX_CONTEXT_LENGTH: 1000000
  },
  'gemini-2.5-flash': {
    MIN_TEMPERATURE: 0.0,
    MAX_TEMPERATURE: 1.0,
    MAX_TOKEN_OUTPUT_LENGTH: 65536,
    MAX_CONTEXT_LENGTH: 1000000
  },
  'gemini-3-pro-preview': {
    MIN_TEMPERATURE: 0.0,
    MAX_TEMPERATURE: 1.0,
    MAX_TOKEN_OUTPUT_LENGTH: 64000,
    MAX_CONTEXT_LENGTH: 1000000
  },
  'gemini-3.1-pro-preview': {
    MIN_TEMPERATURE: 0.0,
    MAX_TEMPERATURE: 1.0,
    MAX_TOKEN_OUTPUT_LENGTH: 64000,
    MAX_CONTEXT_LENGTH: 1000000
  },
  'gemini-3-pro-image-preview': {
    MIN_TEMPERATURE: 0.0,
    MAX_TEMPERATURE: 1.0,
    MAX_TOKEN_OUTPUT_LENGTH: 32000,
    MAX_CONTEXT_LENGTH: 65000
  },
  'gemini-3.5-flash': {
    MIN_TEMPERATURE: 0.0,
    MAX_TEMPERATURE: 1.0,
    MAX_TOKEN_OUTPUT_LENGTH: 65536,
    MAX_CONTEXT_LENGTH: 1000000
  },
  'gemini-3.8-flash': {
    MIN_TEMPERATURE: 0.0,
    MAX_TEMPERATURE: 1.0,
    MAX_TOKEN_OUTPUT_LENGTH: 65536,
    MAX_CONTEXT_LENGTH: 1000000
  },

  // MISTRAL MODELS
  'mistral-tiny': {
    MIN_TEMPERATURE: 0.0,
    MAX_TEMPERATURE: 1.0,
    MAX_TOKEN_OUTPUT_LENGTH: 2000,
    MAX_CONTEXT_LENGTH: 8000
  },
  'mistral-small': {
    MIN_TEMPERATURE: 0.0,
    MAX_TEMPERATURE: 1.0,
    MAX_TOKEN_OUTPUT_LENGTH: 2000,
    MAX_CONTEXT_LENGTH: 32000
  },
  'mistral-medium': {
    MIN_TEMPERATURE: 0.0,
    MAX_TEMPERATURE: 1.0,
    MAX_TOKEN_OUTPUT_LENGTH: 2000,
    MAX_CONTEXT_LENGTH: 32000
  },
  'mistral-small-latest': {
    MIN_TEMPERATURE: 0.0,
    MAX_TEMPERATURE: 1.0,
    MAX_TOKEN_OUTPUT_LENGTH: 32768,
    MAX_CONTEXT_LENGTH: 128000
  },
  'mistral-medium-latest': {
    MIN_TEMPERATURE: 0.0,
    MAX_TEMPERATURE: 1.0,
    MAX_TOKEN_OUTPUT_LENGTH: 32768,
    MAX_CONTEXT_LENGTH: 128000
  },
  'mistral-large-latest': {
    MIN_TEMPERATURE: 0.0,
    MAX_TEMPERATURE: 1.0,
    MAX_TOKEN_OUTPUT_LENGTH: 32768,
    MAX_CONTEXT_LENGTH: 128000
  },

  // GROQ MODELS
  'mixtral-8x7b-32768': {
    MIN_TEMPERATURE: 0.0,
    MAX_TEMPERATURE: 1.0,
    MAX_TOKEN_OUTPUT_LENGTH: 4096,
    MAX_CONTEXT_LENGTH: 32768
  },
  'llama3-70b-8192': {
    MIN_TEMPERATURE: 0.0,
    MAX_TEMPERATURE: 1.0,
    MAX_TOKEN_OUTPUT_LENGTH: 8192,
    MAX_CONTEXT_LENGTH: 8192
  },
  'llama3-8b-8192': {
    MIN_TEMPERATURE: 0.0,
    MAX_TEMPERATURE: 1.0,
    MAX_TOKEN_OUTPUT_LENGTH: 8192,
    MAX_CONTEXT_LENGTH: 8192
  },
  'openai/gpt-oss-120b': {
    MIN_TEMPERATURE: 0.0,
    MAX_TEMPERATURE: 1.0,
    MAX_TOKEN_OUTPUT_LENGTH: 65536,
    MAX_CONTEXT_LENGTH: 131072
  },
  'openai/gpt-oss-20b': {
    MIN_TEMPERATURE: 0.0,
    MAX_TEMPERATURE: 1.0,
    MAX_TOKEN_OUTPUT_LENGTH: 65536,
    MAX_CONTEXT_LENGTH: 131072
  },
  'qwen/qwen3.6-27b': {
    MIN_TEMPERATURE: 0.0,
    MAX_TEMPERATURE: 1.0,
    MAX_TOKEN_OUTPUT_LENGTH: 32768,
    MAX_CONTEXT_LENGTH: 131072
  },

  // Marshmallow Losi MODELS (losi-omega backed by GPT-5.5-class: 128k output, 1.05M context)
  'losi-omega': {
    MIN_TEMPERATURE: 0.0,
    MAX_TEMPERATURE: 2.0,
    MAX_TOKEN_OUTPUT_LENGTH: 128000,
    MAX_CONTEXT_LENGTH: 1050000
  },
  'losi-mini': {
    MIN_TEMPERATURE: 0.0,
    MAX_TEMPERATURE: 2.0,
    MAX_TOKEN_OUTPUT_LENGTH: 12000,
    MAX_CONTEXT_LENGTH: 100000
  },
  'losi-flash': {
    MIN_TEMPERATURE: 0.0,
    MAX_TEMPERATURE: 2.0,
    MAX_TOKEN_OUTPUT_LENGTH: 32768,
    MAX_CONTEXT_LENGTH: 1000000
  },

  // OPENAI MODELS
  'o1-2024-12-17': {
    MIN_TEMPERATURE: 0.0,
    MAX_TEMPERATURE: 1.0,
    MAX_TOKEN_OUTPUT_LENGTH: 100000,
    MAX_CONTEXT_LENGTH: 200000
  },
  'o1-mini': {
    MIN_TEMPERATURE: 0.0,
    MAX_TEMPERATURE: 1.0,
    MAX_TOKEN_OUTPUT_LENGTH: 65536,
    MAX_CONTEXT_LENGTH: 128000
  },
  'o3-mini': {
    MIN_TEMPERATURE: 0.0,
    MAX_TEMPERATURE: 1.0,
    MAX_TOKEN_OUTPUT_LENGTH: 65536,
    MAX_CONTEXT_LENGTH: 128000
  },
  'gpt-4o-2024-11-20': {
    MIN_TEMPERATURE: 0.0,
    MAX_TEMPERATURE: 2.0,
    MAX_TOKEN_OUTPUT_LENGTH: 16384,
    MAX_CONTEXT_LENGTH: 128000
  },
  'gpt-realtime': {
    MIN_TEMPERATURE: 0.0,
    MAX_TEMPERATURE: 2.0,
    MAX_TOKEN_OUTPUT_LENGTH: 4096,
    MAX_CONTEXT_LENGTH: 128000
  },
  'gpt-realtime-mini-2025-12-15': {
    MIN_TEMPERATURE: 0.0,
    MAX_TEMPERATURE: 2.0,
    MAX_TOKEN_OUTPUT_LENGTH: 4096,
    MAX_CONTEXT_LENGTH: 128000
  },
  'gpt-realtime-2': {
    MIN_TEMPERATURE: 0.0,
    MAX_TEMPERATURE: 2.0,
    MAX_TOKEN_OUTPUT_LENGTH: 4096,
    MAX_CONTEXT_LENGTH: 128000
  },
  'gpt-4o-realtime-preview': {
    MIN_TEMPERATURE: 0.0,
    MAX_TEMPERATURE: 2.0,
    MAX_TOKEN_OUTPUT_LENGTH: 4096,
    MAX_CONTEXT_LENGTH: 128000
  },
  'gpt-4o-mini': {
    MIN_TEMPERATURE: 0.0,
    MAX_TEMPERATURE: 2.0,
    MAX_TOKEN_OUTPUT_LENGTH: 16384,
    MAX_CONTEXT_LENGTH: 128000
  },
  'gpt-4.1': {
    MIN_TEMPERATURE: 0.0,
    MAX_TEMPERATURE: 1.0,
    MAX_TOKEN_OUTPUT_LENGTH: 32768,
    MAX_CONTEXT_LENGTH: 1000000
  },
  'gpt-4.1-mini': {
    MIN_TEMPERATURE: 0.0,
    MAX_TEMPERATURE: 1.0,
    MAX_TOKEN_OUTPUT_LENGTH: 32768,
    MAX_CONTEXT_LENGTH: 1000000
  },
  'gpt-4.1-nano': {
    MIN_TEMPERATURE: 0.0,
    MAX_TEMPERATURE: 1.0,
    MAX_TOKEN_OUTPUT_LENGTH: 32768,
    MAX_CONTEXT_LENGTH: 1000000
  },
  'gpt-5': {
    MIN_TEMPERATURE: 0.0,
    MAX_TEMPERATURE: 1.0,
    MAX_TOKEN_OUTPUT_LENGTH: 32768,
    MAX_CONTEXT_LENGTH: 250000
  },
  'gpt-5.1': {
    MIN_TEMPERATURE: 0.0,
    MAX_TEMPERATURE: 1.0,
    MAX_TOKEN_OUTPUT_LENGTH: 32768,
    MAX_CONTEXT_LENGTH: 250000
  },
  'gpt-5.1-codex-max': {
    MIN_TEMPERATURE: 0.0,
    MAX_TEMPERATURE: 1.0,
    MAX_TOKEN_OUTPUT_LENGTH: 32768,
    MAX_CONTEXT_LENGTH: 500000
  },
  'gpt-5.2': {
    MIN_TEMPERATURE: 0.0,
    MAX_TEMPERATURE: 1.0,
    MAX_TOKEN_OUTPUT_LENGTH: 32768,
    MAX_CONTEXT_LENGTH: 500000
  },
  'gpt-5.2-chat-latest': {
    MIN_TEMPERATURE: 0.0,
    MAX_TEMPERATURE: 1.0,
    MAX_TOKEN_OUTPUT_LENGTH: 32768,
    MAX_CONTEXT_LENGTH: 500000
  },
  'gpt-5.2-pro': {
    MIN_TEMPERATURE: 0.0,
    MAX_TEMPERATURE: 1.0,
    MAX_TOKEN_OUTPUT_LENGTH: 32768,
    MAX_CONTEXT_LENGTH: 500000
  },
  'gpt-5.5': {
    MIN_TEMPERATURE: 0.0,
    MAX_TEMPERATURE: 1.0,
    MAX_TOKEN_OUTPUT_LENGTH: 128000,
    MAX_CONTEXT_LENGTH: 1050000
  },
  'gpt-5.6-sol': {
    MIN_TEMPERATURE: 0.0,
    MAX_TEMPERATURE: 1.0,
    MAX_TOKEN_OUTPUT_LENGTH: 128000,
    MAX_CONTEXT_LENGTH: 1050000
  },
  'gpt-5.6-terra': {
    MIN_TEMPERATURE: 0.0,
    MAX_TEMPERATURE: 1.0,
    MAX_TOKEN_OUTPUT_LENGTH: 128000,
    MAX_CONTEXT_LENGTH: 1050000
  },
  'gpt-5.6-luna': {
    MIN_TEMPERATURE: 0.0,
    MAX_TEMPERATURE: 1.0,
    MAX_TOKEN_OUTPUT_LENGTH: 65536,
    MAX_CONTEXT_LENGTH: 1050000
  },
  'gpt-5.4': {
    MIN_TEMPERATURE: 0.0,
    MAX_TEMPERATURE: 1.0,
    MAX_TOKEN_OUTPUT_LENGTH: 128000,
    MAX_CONTEXT_LENGTH: 1050000
  },
  'gpt-5.4-mini': {
    MIN_TEMPERATURE: 0.0,
    MAX_TEMPERATURE: 1.0,
    MAX_TOKEN_OUTPUT_LENGTH: 32768,
    MAX_CONTEXT_LENGTH: 1050000
  },
  'gpt-5.4-pro': {
    MIN_TEMPERATURE: 0.0,
    MAX_TEMPERATURE: 1.0,
    MAX_TOKEN_OUTPUT_LENGTH: 128000,
    MAX_CONTEXT_LENGTH: 1050000
  },
  'gpt-5-mini': {
    MIN_TEMPERATURE: 0.0,
    MAX_TEMPERATURE: 1.0,
    MAX_TOKEN_OUTPUT_LENGTH: 16384,
    MAX_CONTEXT_LENGTH: 250000
  },
  'gpt-5-nano': {
    MIN_TEMPERATURE: 0.0,
    MAX_TEMPERATURE: 1.0,
    MAX_TOKEN_OUTPUT_LENGTH: 8192,
    MAX_CONTEXT_LENGTH: 250000
  },

  // AWS BEDROCK VOICE MODELS
  // Nova Sonic 2 is a speech-to-speech model; the "context" and "output" caps
  // reflect its inference config knobs (maxTokens ceiling) rather than a
  // literal text context window.
  'amazon.nova-2-sonic-v1:0': {
    MIN_TEMPERATURE: 0.0,
    MAX_TEMPERATURE: 1.0,
    MAX_TOKEN_OUTPUT_LENGTH: 8192,
    MAX_CONTEXT_LENGTH: 32000
  },

  // AWS BEDROCK CHAT MODELS (GLM/DeepSeek/Kimi/Nova Pro)
  // Figures below are best-effort from public model cards, not the Bedrock
  // console — re-verify exact caps there if they matter for billing.
  // Bedrock Mantle GLM rejects max_tokens above 64000 (live error:
  // "exceeds the model limit of 64000"). Do not use the public 128k card here.
  'zai.glm-4.7': {
    MIN_TEMPERATURE: 0.0,
    MAX_TEMPERATURE: 1.0,
    MAX_TOKEN_OUTPUT_LENGTH: 64000,
    MAX_CONTEXT_LENGTH: 200000
  },
  'zai.glm-4.7-flash': {
    MIN_TEMPERATURE: 0.0,
    MAX_TEMPERATURE: 1.0,
    MAX_TOKEN_OUTPUT_LENGTH: 64000,
    MAX_CONTEXT_LENGTH: 200000
  },
  'deepseek.v3.2': {
    MIN_TEMPERATURE: 0.0,
    MAX_TEMPERATURE: 1.0,
    MAX_TOKEN_OUTPUT_LENGTH: 64000,
    MAX_CONTEXT_LENGTH: 262144
  },
  'moonshotai.kimi-k2.5': {
    MIN_TEMPERATURE: 0.0,
    MAX_TEMPERATURE: 1.0,
    MAX_TOKEN_OUTPUT_LENGTH: 32768,
    MAX_CONTEXT_LENGTH: 256000
  },
  // Kimi K3 on Bedrock (global CRIS). Live probe 2026-09-30 confirmed text,
  // tool_calls, and image_url input. Prefer Chat Completions over Converse.
  'global.moonshotai.kimi-k3': {
    MIN_TEMPERATURE: 0.0,
    MAX_TEMPERATURE: 1.0,
    MAX_TOKEN_OUTPUT_LENGTH: 65536,
    MAX_CONTEXT_LENGTH: 1048576
  },
  // Live-probed 2026-09-30 on Chat Completions (text + tools + images).
  'qwen.qwen3-vl-235b-a22b': {
    MIN_TEMPERATURE: 0.0,
    MAX_TEMPERATURE: 1.0,
    MAX_TOKEN_OUTPUT_LENGTH: 32768,
    MAX_CONTEXT_LENGTH: 262144
  },
  'qwen.qwen3-coder-next': {
    MIN_TEMPERATURE: 0.0,
    MAX_TEMPERATURE: 1.0,
    MAX_TOKEN_OUTPUT_LENGTH: 32768,
    MAX_CONTEXT_LENGTH: 262144
  },
  'qwen.qwen3-next-80b-a3b': {
    MIN_TEMPERATURE: 0.0,
    MAX_TEMPERATURE: 1.0,
    MAX_TOKEN_OUTPUT_LENGTH: 32768,
    MAX_CONTEXT_LENGTH: 262144
  },
  // MiniMax M2.5/M2.1 — text + tools; no image modality on Bedrock.
  'minimax.minimax-m2.5': {
    MIN_TEMPERATURE: 0.0,
    MAX_TEMPERATURE: 1.0,
    MAX_TOKEN_OUTPUT_LENGTH: 8192,
    MAX_CONTEXT_LENGTH: 196000
  },
  'minimax.minimax-m2.1': {
    MIN_TEMPERATURE: 0.0,
    MAX_TEMPERATURE: 1.0,
    MAX_TOKEN_OUTPUT_LENGTH: 8192,
    MAX_CONTEXT_LENGTH: 196000
  },
  'openai.gpt-oss-120b-1:0': {
    MIN_TEMPERATURE: 0.0,
    MAX_TEMPERATURE: 1.0,
    MAX_TOKEN_OUTPUT_LENGTH: 32768,
    MAX_CONTEXT_LENGTH: 131072
  },
  // Nova Pro Bedrock model card (current): Max output tokens 5K.
  // Older catalog notes said 10K; requesting above 5K → ValidationException.
  'amazon.nova-pro-v1:0': {
    MIN_TEMPERATURE: 0.0,
    MAX_TEMPERATURE: 1.0,
    MAX_TOKEN_OUTPUT_LENGTH: 5000,
    MAX_CONTEXT_LENGTH: 300000
  },

  // PERPLEXITY MODELS
  sonar: {
    MIN_TEMPERATURE: 0.0,
    MAX_TEMPERATURE: 2.0,
    MAX_TOKEN_OUTPUT_LENGTH: 4096, // Assuming a default value, adjust if needed
    MAX_CONTEXT_LENGTH: 127072
  },
  'sonar-pro': {
    MIN_TEMPERATURE: 0.0,
    MAX_TEMPERATURE: 2.0,
    MAX_TOKEN_OUTPUT_LENGTH: 8096, // Assuming a default value, adjust if needed
    MAX_CONTEXT_LENGTH: 200000
  },
  'sonar-reasoning': {
    MIN_TEMPERATURE: 0.0,
    MAX_TEMPERATURE: 2.0,
    MAX_TOKEN_OUTPUT_LENGTH: 4096, // Assuming a default value, adjust if needed
    MAX_CONTEXT_LENGTH: 127072
  },
  'sonar-reasoning-pro': {
    MIN_TEMPERATURE: 0.0,
    MAX_TEMPERATURE: 2.0,
    MAX_TOKEN_OUTPUT_LENGTH: 8096, // Assuming a default value, adjust if needed
    MAX_CONTEXT_LENGTH: 127072
  },
  'sonar-deep-research': {
    MIN_TEMPERATURE: 0.0,
    MAX_TEMPERATURE: 2.0,
    MAX_TOKEN_OUTPUT_LENGTH: 4096, // Assuming a default value, adjust if needed
    MAX_CONTEXT_LENGTH: 59000
  }
}

/**
 * Clamp a requested completion budget to the target model's output cap.
 * Rate-limit / capacity fallbacks often reuse the primary model's
 * maxOutputLength (e.g. Claude 128k) — Bedrock then 400s with
 * "exceeds the model limit of 64000" for GLM/Grok/DeepSeek.
 */
export function clampMaxOutputTokens(
  model: string,
  requested: number | null | undefined
): number {
  const requestedTokens =
    typeof requested === 'number' && Number.isFinite(requested) && requested > 0
      ? Math.floor(requested)
      : DEFAULT_MAX_TOKEN_OUTPUT_LENGTH
  const modelCap =
    CHAT_SETTING_LIMITS[model as LLMID]?.MAX_TOKEN_OUTPUT_LENGTH ??
    DEFAULT_MAX_TOKEN_OUTPUT_LENGTH
  return Math.min(requestedTokens, modelCap)
}
